// External libraries
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { BatchHttpLink } from 'apollo-link-batch-http';
// @ts-expect-error untyped-import
import { isString } from '@checkout/react-components';

// Internal modules
import { logger } from '@/lib/logger';
import { getClientMetadataID } from '@/lib/utils';
import { getAccessToken, getGlobals, getLocale, getToken } from '@/lib/globals';
import { X_APP_NAME } from '@/lib/constants';
import type { GenericObjectType } from '@/types';

// Static headers - these will not change during the course of the transaction
const BASE_HEADERS = {
  'paypal-client-context': getToken(),
  'paypal-client-metadata-id': getClientMetadataID(),
  'x-app-name': X_APP_NAME,
};

// Replacing the trailing `/` so that the `graphql?<OPERATION_NAME>`
// will be shown in the network tab instead of just `<OPERATION_NAME>`
const BASE_URI = getGlobals().uri?.queryEndpoint?.replace(/\/$/, '');
const getDescriptiveUri = ({
  isBatchedQuery,
  operationName,
  uri,
}: {
  isBatchedQuery: boolean;
  operationName: string;
  uri: string;
}) => {
  if (isBatchedQuery) {
    return `${BASE_URI}?BatchedQuery`;
  }

  return isString(uri) ? uri : `${BASE_URI}?${operationName}`;
};

// operationName is only a label; the document kind is what distinguishes
// mutations from queries so we can attach x-paypal-signed-token to writes only.
const isMutationOperation = (operation: {
  query: { definitions: readonly { kind: string; operation?: string }[] };
}): boolean =>
  operation.query.definitions.some(
    definition =>
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'mutation',
  );

// This link will dynamically set headers that may change during the
// course of the transaction. (e.g. locale headers, auth headers, etc.)
// It will also append the operation name as a query param to the URI so
// the entry in the network tab of the dev tools is easily identifiable.
const httpContextLink = new ApolloLink((operation, forward) => {
  const { country, locale } = getLocale();
  const { value: accessToken } = getAccessToken();
  const { jwtSignedToken } = getGlobals();
  const { operationName } = operation;

  operation.setContext(
    ({
      isBatchedQuery,
      headers,
      uri,
    }: {
      isBatchedQuery: boolean;
      headers: GenericObjectType;
      uri: string;
    }) => ({
      headers: {
        ...BASE_HEADERS,

        // Headers passed as part of the context for the query/mutation
        ...headers,

        // Conditionally set auth token
        ...(accessToken && { 'x-paypal-internal-euat': accessToken }),

        ...(isMutationOperation(operation) &&
          jwtSignedToken && {
            'x-paypal-signed-token': jwtSignedToken,
          }),

        // Used by xobuyernodeserv for locale resolution
        'x-country': country,
        'x-locale': locale,
      },

      // e.g. `https://www.paypal.com/graphql?SomeQuery`
      uri: getDescriptiveUri({ isBatchedQuery, operationName, uri }),
    }),
  );

  return forward(operation);
});

// Link that modifies request body based on context
const requestTransformLink = new ApolloLink((operation, forward) => {
  const { fraudNetSyncData } = operation.getContext();

  if (fraudNetSyncData) {
    // Create a custom HTTP link with fetch override for this specific request
    const customHttpLink = createHttpLink({
      credentials: 'include',
      fetch: (uri: any, options: any) => {
        if (options && options.body) {
          try {
            const body = JSON.parse(options.body);

            const modifiedBody = {
              ...body,
              fn_sync_data: fraudNetSyncData,
            };

            options.body = JSON.stringify(modifiedBody);
          } catch (error) {
            const message =
              error instanceof Error ? error.message : String(error);
            logger.warn('fn_sync_data_parsing_error', {
              message,
              operation: operation.operationName,
            });
          }
        }

        return fetch(uri, options);
      },
      uri: BASE_URI,
    });

    return customHttpLink.request(operation);
  }

  return forward(operation);
});

const baseHttpLink = createHttpLink({
  credentials: 'include',
  uri: BASE_URI,
});

const batchHttpLink = new BatchHttpLink({
  credentials: 'include',
  uri: BASE_URI,
});

export const httpLink = httpContextLink.split(
  operation => !operation.getContext().isBatchedQuery,
  requestTransformLink.concat(baseHttpLink),
  batchHttpLink,
);
